import { z } from 'zod';
import {
  BakerySettings,
  Closure,
  ClosureDayBody,
  ClosureDayResult,
  ClosureRangeBody,
  ClosureRangeResult,
  PickupWindowRule,
} from '../bakery/index';
import {
  BasisPoints,
  Email,
  ImageUrl,
  Instant,
  Latitude,
  Longitude,
  MinorUnitsNonNegative,
  Ok,
  PageRequest,
  PhoneE164,
  pageOf,
  QrPortfolioSheet,
  ServiceDay,
  TimeZone,
  Uuid,
} from '../common';
import {
  DevicePinLockoutCleared,
  DevicePinReset,
  DevicePinSetByAdmin,
  DeviceRevoked,
  LocationPairing,
  PairingCodeIssued,
  PairingMessageSent,
  SendPairingMessage,
  SetDevicePinByAdmin,
} from '../device-pairing';
import {
  BakeryStorefrontMode,
  OrganizationType,
  PartnerOrgType,
  PayoutVerificationState,
  StorefrontMode,
  SuspensionReason,
  TaxKind,
} from '../enums';
import { FilterFacet } from '../facets';
import { defineEndpoint } from '../http';
import { OrganizationSummary } from '../models';
import { LegalIdentityFields } from '../partner/index';
import { patchSchema } from '../patch';
import { organizationStanding } from '../projections/organization-standing';
import { PICKUP_REQUIREMENTS } from '../projections/pickup-readiness';
import { ChosenStorefrontMode } from '../projections/storefront-modes';

/**
 * The organization's own key: the id the platform stores for it, which is what
 * every audit row names it by, what a seed writes, and what `x-dev-org` scopes
 * a staff session to.
 *
 * It is the same value as `OrganizationSummary.id` in both runtimes, and that
 * is new. The wire id used to be `sha256(stored id)` reshaped into uuid form
 * (`asWireUuid` in the worker) wherever the stored id was not already a uuid -
 * a one-way hash with no reverse, so an operator who copied it off a row could
 * reach the organization with it nowhere: not as a header, not as a lookup, not
 * as a support query. That was true only of the memory runtime, whose ids were
 * readable strings (`part_1`); it mints uuid keys now, so the hash is gone and
 * so is the divergence.
 *
 * Bounded and character-restricted rather than free text: it is an identifier,
 * it travels in a path segment, and a value outside this shape is not one this
 * platform ever minted. Deliberately still not `Uuid` - `x-dev-org: part_1` and
 * the readable names a script prints resolve by derivation, and this schema is
 * the boundary those pass through.
 */
export const OrganizationRef = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/);

/**
 * What operations sees on an organization, over what every portal sees.
 *
 * The two apartment counts are the same pair the owner reads on their own
 * overview (CON-001, WEB-006), taken over the same set and by the same rule, so
 * the two portals cannot disagree about how big a portfolio is: `listed` is the
 * apartments still IN the portfolio - archiving is terminal and an archived
 * apartment is not one the owner has - and `servable` is how many of those a
 * bakery and a courier actually reach (GST-040), derived beside the partner's
 * standing rather than read off that column alone.
 *
 * Both are 0 on a bakery, which holds no portfolio. They live here rather than
 * on the shared `OrganizationSummary` because no other portal asks this
 * question about somebody else's portfolio.
 */
export const InternalOrganizationSummary = OrganizationSummary.extend({
  /** The organization's own key - see `OrganizationRef` above. */
  reference: OrganizationRef,
  /**
   * Why the organization is Suspended; empty when it is not
   * (docs/finance/onboarding.html#suspension, `suspensionReasons`). `suspended`
   * stays the operator's own switch, which the Suspend and Reinstate actions
   * read; this is the ONE tag bakers and partners carry.
   */
  suspendedFor: z.array(SuspensionReason),
  propertiesListed: z.int().nonnegative(),
  propertiesServable: z.int().nonnegative(),
  /**
   * How many bakery locations the organization has. 0 on a property
   * organization, which holds none.
   *
   * The Mode B houses tab is a list of organizations whose one quantitative
   * column is this count, so it must be derived ONCE for the whole page and
   * batched - the same derivation `getOrganizationInfo` already answers for a
   * single organization, never a query per row.
   */
  locationsCount: z.int().nonnegative(),
  /**
   * Which apartment modes a mode B house serves - `B` (B1), `B2`, both or
   * neither (OPS-013, founder 2026-10-06) - in display order. Its shops follow
   * it. The Mode B houses table draws one tick box per mode from it. A mode A
   * house reads `['A']`, which is not a choice, and a partner organization
   * reads `[]`.
   */
  servesModes: z.array(StorefrontMode),
});
export type InternalOrganizationSummary = z.infer<typeof InternalOrganizationSummary>;

/**
 * One tick box of the Mode B houses table: does this house serve apartments on
 * this mode (OPS-013, ALC-002)? One mode per call, added or removed, so two
 * boxes pressed in quick succession on one row are two independent writes
 * rather than two whole sets racing to be last. Its shops follow the house.
 * Refused (409) on a mode outside the house's own pool - a mode A house serves
 * mode A by being one, and has nothing to tick.
 */
export const setOrganizationServedMode = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations/:organizationId/served-modes',
  mount: 'internal',
  summary: 'Tick or untick one apartment mode a mode B house serves (B1 or B2); audited (OPS-013)',
  params: z.object({ organizationId: OrganizationRef }),
  body: z.object({ mode: StorefrontMode, served: z.boolean() }),
  idempotent: true,
  response: InternalOrganizationSummary,
});

export const suspendOrganization = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations/:organizationId/suspend',
  mount: 'internal',
  summary: 'Stop new orders from an organization QR and hold pending payouts; audited (OPS-003)',
  params: z.object({ organizationId: OrganizationRef }),
  body: z.object({ suspended: z.boolean() }),
  idempotent: true,
  response: InternalOrganizationSummary,
});

/**
 * OPS-003: operations creates organizations. A bakery's storefront mode is
 * immutable, set here at creation (OPS-013); the refine below is what makes
 * "a bakery without a mode" unrepresentable on the wire.
 */
export const createOrganization = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations',
  mount: 'internal',
  summary: 'Create a partner or bakery organization; a bakery is born with its mode (OPS-003)',
  body: z
    .object({
      type: z.enum(['BAKERY', 'CONCIERGERIE', 'INDEPENDENT_OWNER']),
      name: z.string().min(1).max(160),
      storefrontMode: BakeryStorefrontMode.optional(),
      /**
       * Which apartment modes a new mode B house serves. Absent is NONE: a new
       * house takes no apartment until operations ticks it on the Mode B
       * houses table, so loading a house never moves an apartment by itself.
       */
      servesModes: z.array(StorefrontMode).max(3).optional(),
    })
    .refine((body) => body.type !== 'BAKERY' || body.storefrontMode !== undefined, {
      message: 'A bakery organization is created with its storefront mode (OPS-013)',
      path: ['storefrontMode'],
    }),
  idempotent: true,
  response: InternalOrganizationSummary,
});

/**
 * One organization as its detail screen reads it (OPS-003): everything the
 * summary says plus the commercial facts and the brand, null where the
 * organization kind has no such fact.
 *
 * Named rather than inline because the Mode B house detail screen reads THIS
 * and writes `OrganizationEditableFields` below - the two are the read and the
 * write halves of one screen, and a field that appears on one and not the other
 * is either unreadable after it is saved or unsettable after it is shown.
 */
/**
 * How often a baker's payouts are approved (PRD PAY-031, docs/finance/payments-and-payouts.html#cycles).
 *
 * Two members, not three: monthly is the rhythm of every conciergerie and
 * independent owner and is never a baker's, so `PayoutCadence` below - which is
 * what a pending payout carries - is a different set and deliberately a
 * different name. They were two anonymous `z.enum`s three lines apart and read
 * as the same one.
 */
export const SettlementCadence = z.enum(['DAILY', 'WEEKLY']);
export type SettlementCadence = z.infer<typeof SettlementCadence>;

/**
 * An organization's code (docs/finance/numbering-and-tags.html#org-code): four
 * letters from its name and two digits, `UTOP01`.
 */
export const OrganizationCode = z.string().regex(/^[A-Z]{4}[0-9]{2}$/);

export const InternalOrganizationInfo = OrganizationSummary.extend({
  /** The organization's own key - see `OrganizationRef` above. */
  reference: OrganizationRef,
  /**
   * The code its cycles, payouts and invoice series are named by; null only
   * for an organization created before codes and not used since.
   */
  code: OrganizationCode.nullable(),
  /** True once the code was used - a document or a payout - and can no longer change. */
  codeFrozen: z.boolean(),
  /** As on the summary row: why it is Suspended, empty when it is not. */
  suspendedFor: z.array(SuspensionReason),
  stripeAccountId: z.string().max(120).nullable(),
  settlementCadence: SettlementCadence.nullable(),
  settlementCadenceEffectiveAt: Instant.nullable(),
  nextSettlementCadence: SettlementCadence.nullable(),
  nextSettlementCadenceEffectiveAt: Instant.nullable(),
  payoutScheduleSyncState: z.enum(['PENDING', 'APPLIED', 'FAILED']).nullable(),
  /*
   * There is no commercial arrangement and no bakery rate here, and there is
   * not meant to be (CAT-001, founder decision 2026-08-29). What a house trades
   * on is its PRICES - one per product, on the Menus tab - and the platform's
   * commission is the gap between each of those and the guest price.
   */
  /** Latest OWNER_COMMISSION version; null for bakeries. */
  commissionRateBasisPoints: BasisPoints.nullable(),
  minimumBasket: MinorUnitsNonNegative.nullable(),
  /** The bakery's own immutable mode (OPS-013); null for partner organizations. */
  storefrontMode: BakeryStorefrontMode.nullable(),
  locationsCount: z.int().nonnegative(),
  /**
   * What the guest reads on the mode B chooser (CAT-012, GST-031) - the same
   * three facts `ShopBrand` carries, answered here so the house detail screen
   * reads one endpoint rather than joining `listShopBrands` to this one. Null
   * on a property organization and on an unbranded bakery.
   */
  brandDisplayName: z.string().max(120).nullable(),
  brandDescription: z.string().max(600).nullable(),
  /* `ImageUrl` both ways: the bytes are refused on the way out as on the way in. */
  brandLogoRef: ImageUrl.nullable(),
  brandImagery: z.array(ImageUrl).max(6).default([]),
});
export type InternalOrganizationInfo = z.infer<typeof InternalOrganizationInfo>;

export const getOrganizationInfo = defineEndpoint({
  method: 'GET',
  path: '/internal/organizations/:organizationId',
  mount: 'internal',
  summary: 'One organization with its commercial configuration and brand (OPS-003, CAT-012)',
  params: z.object({ organizationId: OrganizationRef }),
  response: InternalOrganizationInfo,
});

export const listOrganizations = defineEndpoint({
  method: 'GET',
  path: '/internal/organizations',
  mount: 'internal',
  summary: 'Onboard and manage partner and bakery organizations (OPS-003)',
  query: PageRequest.extend({
    /*
     * A SET of types, not one, and repeated on the wire (`?type=A&type=B`) -
     * `bind.ts` binds an array-typed query key from every occurrence of it. The
     * internal portal reaches this endpoint as two destinations, Bakeries and
     * Properties, and Properties is exactly two types; asking for all of them
     * and dropping the bakeries client-side would silently interact with
     * `limit`, which is applied server-side before anything the client could
     * filter. Omitted still means every type.
     */
    type: z.array(OrganizationType).min(1).optional(),
    /*
     * The bakery's own immutable mode (OPS-013), for the same reason `type` is
     * a server-side filter and by the same argument: the Bakeries houses tab
     * wants the mode B organizations, and asking for every bakery to drop the
     * mode A ones client-side would interact with `limit` exactly as described
     * above - the page would silently come up short once there are more
     * bakeries than one page holds, and look like a complete list. Meaningless
     * on a property organization, which has no mode of its own; omitted means
     * both modes.
     */
    mode: StorefrontMode.optional(),
    search: z.string().max(120).optional(),
  }),
  response: pageOf(InternalOrganizationSummary).extend({
    /**
     * How many organizations carry each type (founder, 2026-09-14: a filter
     * option no row carries is not offered). Counted with `mode` applied and
     * the type choice itself ignored - but only over the rosters the request
     * reads: a property type is counted over the property organizations and
     * BAKERY over the bakeries, so the Properties tab asking for one of its two
     * types still never pays for the bakery roster to count a type it does not
     * offer. A type no organization carries is absent rather than zero.
     */
    facets: z.object({ type: z.array(FilterFacet) }),
  }),
});

/**
 * The fields an operator may edit on an organization (OPS-003). Fed through
 * `patchSchema` so the wire carries PATCH semantics from `../patch`: an omitted
 * key is untouched, a present key is set. None of these is nullable - an
 * organization always has a name, a cap and a default mode - so there is no
 * clear instruction here, and an emptied name box is rejected by `min(1)`
 * rather than silently stored blank.
 */
export const OrganizationEditableFields = z.object({
  /**
   * The organization's code, until its first document or payout
   * (numbering-and-tags#org-code): refused once frozen, when another
   * organization holds it, or when a delivery provider owns it.
   */
  code: OrganizationCode,
  /** House-level principal settlement; changes activate at the next Paris Monday. */
  settlementCadence: SettlementCadence,
  qrGenerationCap: z.int().nonnegative(),
  /**
   * The ONE mode this client is on (OPS-013, founder 2026-09-21): a list for
   * the wire's sake, holding exactly one member. Saving it moves every
   * apartment in the portfolio onto that mode, so the owner is never asked to
   * choose. It was a set of several for three days, and a client saved then
   * still READS as a set - see `ChosenStorefrontMode` for why only the save is
   * held to one.
   */
  allowedStorefrontModes: ChosenStorefrontMode,
  name: z.string().min(1).max(160),
  /**
   * Conciergerie or independent owner (PRD section 7). The two are one entity
   * distinguished by this field, an onboarding mistake is corrected by changing
   * it, and BAKERY is not offered: a bakery is a different entity with a
   * different table shape, so this is a property organization's kind rather
   * than an organization's kind in general.
   */
  type: PartnerOrgType,
  /**
   * The owner's commission (PAY-017, PAY-021), settable where the operator is
   * already reading it rather than only from Finance -> Rates. Like every rate
   * on this platform, setting it appends a NEW `rate_versions` row - it goes
   * through the same `administerRates` the rates screen calls - so an order
   * already placed keeps the split it was placed under (PAY-003).
   */
  commissionRateBasisPoints: BasisPoints,
  /**
   * The organization's standing (OPS-003). Suspending holds pending payouts and
   * stops new orders from its QR codes; the effects are not this field's, they
   * are `suspendOrganization`'s, which is the one implementation both this
   * PATCH and the roster's own suspend button reach.
   */
  suspended: z.boolean(),
  /**
   * The brand group (CAT-012, GST-031), and the ONE endpoint that writes it.
   *
   * It was written from here AND from the catalogue tab's own panel, through a
   * second endpoint with its own body and its own validators. That cost both
   * halves of what one write path buys: a screen saving its name through one
   * endpoint and its display name through another has two chances to
   * half-succeed with no way to tell which half did - and the two schemas had
   * drifted apart on the same field, so a logo one screen accepted was one the
   * other refused. The second endpoint is gone; the catalogue tab links to the
   * house record rather than editing it beside the shelf.
   *
   * `brandLogoRef` and each entry of `brandImagery` are the URLs `uploadMedia`
   * returns - a reference to stored bytes, never the bytes. `ImageUrl` rather
   * than `z.url()` is what makes that true at the boundary instead of by
   * convention: `z.url()` accepts a `data:` URI (see `common.ts`), and this was
   * the last image-carrying request body on the platform that still did.
   */
  brandDisplayName: z.string().min(1).max(120),
  brandDescription: z.string().max(600),
  brandLogoRef: ImageUrl.nullable(),
  brandImagery: z.array(ImageUrl).max(6),
  /**
   * The one commercial control a house carries here (CAT-014).
   *
   * The arrangement and the bakery share went with the revenue share on
   * 2026-08-29: a house is on PRICES now, administered per product on the Menus
   * tab, so there is neither a model to choose nor a rate to version here.
   *
   * `minimumBasket` is nullable, and the null is meaningful under PATCH
   * semantics: an emptied box means "no minimum on this house", which is a
   * different instruction from leaving the field alone.
   */
  minimumBasket: MinorUnitsNonNegative.nullable(),
});
export type OrganizationEditableFields = z.infer<typeof OrganizationEditableFields>;

export const updateOrganization = defineEndpoint({
  method: 'PATCH',
  path: '/internal/organizations/:organizationId',
  mount: 'internal',
  summary: 'Record the configuration resulting from an off-platform agreement (OPS-003, OPS-015)',
  params: z.object({ organizationId: OrganizationRef }),
  // Every change to an organization's commercial configuration is audited with
  // the operator and the before/after snapshots (OPS-003, OPS-008). The
  // operator is never asked to type a reason for it.
  body: patchSchema(OrganizationEditableFields),
  idempotent: false,
  response: InternalOrganizationInfo,
});

/**
 * OPS-003, and the delete rule of the plan's section 2.1.
 *
 * "Permanently delete" means delete only what was never used. An order
 * snapshots its bakery, product names, prices and commission at placement
 * (CAT-006) and `bakery_locations` is declared `onDelete: 'restrict'`, so
 * removing a record that has traded would orphan settled orders and payout
 * statements - the ledger, not a listing. The server therefore REFUSES when the
 * organization has ever carried an order, and the operator suspends instead.
 * The onboarding mistake this exists for - a house typed in wrong on Tuesday -
 * has no orders and deletes cleanly.
 *
 * The refusal is server-side and is the enforcement; the disabled menu entry in
 * the portal is an affordance that saves a round trip, never the check.
 */
export const deleteOrganization = defineEndpoint({
  method: 'DELETE',
  path: '/internal/organizations/:organizationId',
  mount: 'internal',
  summary: 'Delete an organization that never carried an order; refused otherwise (OPS-003)',
  params: z.object({ organizationId: OrganizationRef }),
  idempotent: false,
  response: Ok,
});

// ---------------------------------------------------------------------------
// One client's apartments (PRD OPS-003, CON-001)
// ---------------------------------------------------------------------------

/**
 * An apartment as the client's own record shows it.
 *
 * Deliberately the OWNER's facts rather than a second portfolio screen: what an
 * operator taking a call about a client needs is which apartments they have,
 * whether each one can be served, where its printed code is in its life, and
 * the two things that answer "is the code on that wall the right one" - the
 * sheet it is printed on, and the storefront it opens.
 *
 * `guestOrderingUrl` IS the printed `/q/<token>` entry (TST-008), not a
 * reconstruction of it: the token is signed without the host (OPS-020), so this
 * is the exact link the code on the wall resolves to. Null when there is no
 * active code, because then there is no such link - an operator must be able to
 * tell "no code yet" from "a code that opens nothing".
 */
export const InternalPropertyRow = z.object({
  id: Uuid,
  /** The apartment's own display name, as the owner set it. */
  reference: z.string().max(200),
  address: z.string().max(400),
  /** The owner's own three states (CON-001); archiving is terminal. */
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
  /** The partner's standing AND derived coverage (GST-040), never the column alone. */
  servable: z.boolean(),
  /** The mode this apartment's code carries, or the org default. */
  storefrontMode: StorefrontMode,
  /** Does the apartment hold its one permanent code yet (CON-003)? */
  hasQrCode: z.boolean(),
  guestOrderingUrl: z.url().nullable(),
});
export type InternalPropertyRow = z.infer<typeof InternalPropertyRow>;

/**
 * The portfolio, and how big it is - one page of apartments plus the two counts
 * the client's status strip states (OPS-003, CON-001, GST-040).
 *
 * The counts are over the WHOLE portfolio rather than over `items`, which is
 * one page of it, and they are aggregated from the very rows this endpoint has
 * just derived - `listed` is the rows whose status is not ARCHIVED (archiving
 * is terminal and an archived apartment is not one the client has) and
 * `servable` is how many of those carry the derived coverage answer each row
 * already reports. That is why they live here and not on the organization's own
 * record: this is the only endpoint that already knows both facts about every
 * apartment, so counting them costs nothing, and a second endpoint deriving the
 * same pair would be a second opinion - and a coverage probe over the whole
 * portfolio on every organization save, which is what it cost when it was tried
 * (`action-budget.test.ts`, 'Edit an organization').
 */
export const InternalPropertyPage = pageOf(InternalPropertyRow).extend({
  listed: z.int().nonnegative(),
  servable: z.int().nonnegative(),
});
export type InternalPropertyPage = z.infer<typeof InternalPropertyPage>;

// ---------------------------------------------------------------------------
// Onboarding a house to payouts (PRD OPS-036, BAK-061, PAY-029)
//
// The operator records the company from the Kbis, presses one button, and the
// platform creates the house's recipient account and emails the baker a link of
// the platform's own. Everything here answers the SAME shape - the read, the
// save, the send and the revoke - because all four are one screen, and a field
// one of them omitted would be a screen that goes blank after a save.
// ---------------------------------------------------------------------------

/** Which required fact is still missing, in the order an operator fills them. */
export const PayoutOnboardingRequirement = z.enum([
  'legalName',
  'legalForm',
  'siren',
  'registeredOffice',
  'taxResidence',
  'bankCountry',
  'contactEmail',
]);
export type PayoutOnboardingRequirement = z.infer<typeof PayoutOnboardingRequirement>;

export const PayoutOnboardingLinkState = z.enum(['LIVE', 'EXPIRED', 'REVOKED', 'COMPLETED']);
export type PayoutOnboardingLinkState = z.infer<typeof PayoutOnboardingLinkState>;

/**
 * Everything the Payouts card on a house prints, and the Legal identity card
 * above it edits.
 *
 * `send` is answered by the SERVER, not derived on the screen: whether a link
 * may be sent and what is missing is the platform's rule
 * (`payoutOnboardingReadiness`), and a rule restated in a frontend is a rule
 * that is wrong in one of the places it lives. `createsAccount` is what decides
 * whether the button asks for a confirmation - the dashboard type is permanent
 * per account (PAY-029), so the FIRST send deserves one click of thought and a
 * resend does not.
 *
 * The link block holds no token and no nonce, here or anywhere a person can
 * read: what an operator needs is when it went, to whom, by whom, and whether
 * anybody has opened it.
 */
export const PayoutOnboarding = z.object({
  identity: LegalIdentityFields,
  /**
   * The kind every rule reads - the stated `taxKind`, or on a row that predates
   * the question the one the platform derives - exactly as the partner's own
   * `getLegalIdentity` answers it, so Internal and the owner's portal draw the
   * same boxes for the same payee rather than each deciding.
   */
  effectiveTaxKind: TaxKind,
  contactEmail: Email.nullable(),
  payoutVerification: PayoutVerificationState,
  stripeAccountId: z.string().max(120).nullable(),
  /**
   * WEB-006 (founder, 2026-09-30): true once the organization holds an account
   * Stripe has VERIFIED - from then on what Stripe verified is fixed for
   * everybody, operators included. Before that nothing is locked: a change
   * retires the unverified account and the next send creates a new one with
   * the new details. Said by the server, so the card never re-derives it.
   */
  identityLocked: z.boolean(),
  link: z
    .object({
      sentAt: Instant,
      sentTo: Email,
      sentBy: z.string().max(160),
      expiresAt: Instant,
      state: PayoutOnboardingLinkState,
      mintCount: z.int().nonnegative(),
      lastOpenedAt: Instant.nullable(),
    })
    .nullable(),
  requirements: z
    .object({
      blockedReason: z.string().max(120).nullable(),
      currentlyDue: z.array(z.string().max(120)),
      pastDue: z.array(z.string().max(120)),
      currentDeadline: Instant.nullable(),
      detailsSubmitted: z.boolean(),
      observedAt: Instant,
    })
    .nullable(),
  send: z.object({
    allowed: z.boolean(),
    missing: z.array(PayoutOnboardingRequirement),
    /** True until the house carries an account: the first send creates one. */
    createsAccount: z.boolean(),
  }),
});
export type PayoutOnboarding = z.infer<typeof PayoutOnboarding>;

export const getPayoutOnboarding = defineEndpoint({
  method: 'GET',
  path: '/internal/organizations/:organizationId/payout-onboarding',
  mount: 'internal',
  summary: "One organization's legal identity, payout account and onboarding link (OPS-036)",
  params: z.object({ organizationId: OrganizationRef }),
  response: PayoutOnboarding,
});

export const updateLegalIdentity = defineEndpoint({
  method: 'PATCH',
  path: '/internal/organizations/:organizationId/legal-identity',
  mount: 'internal',
  summary: "Record a bakery house's legal identity and contact email; audited (OPS-036, CON-013)",
  params: z.object({ organizationId: OrganizationRef }),
  /**
   * `patchSchema` rather than `.partial()`, because THE RULE
   * (`packages/contracts/src/patch.ts`) is never restated per route: omitted is
   * untouched, an explicit `null` clears, a value sets.
   *
   * A PROPERTY partner's identity stays its own to state on `/c` (CON-013), so
   * this refuses one; a bakery is the exception, because operations holds the
   * register facts from the commercial conversation and needs them to create
   * the house's payout account. The self-billing fields are absent on purpose:
   * a bakery has no mandate to accept.
   */
  body: patchSchema(LegalIdentityFields.extend({ contactEmail: Email.nullable() })),
  idempotent: false,
  response: PayoutOnboarding,
});

export const sendPayoutOnboardingLink = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations/:organizationId/payout-onboarding/send',
  mount: 'internal',
  summary: "Create the house's payout account if needed, then email a fresh link (OPS-036)",
  params: z.object({ organizationId: OrganizationRef }),
  idempotent: true,
  response: PayoutOnboarding,
});

export const revokePayoutOnboardingLink = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations/:organizationId/payout-onboarding/revoke',
  mount: 'internal',
  summary: 'Kill the live onboarding link a leaked email exposed (OPS-036)',
  params: z.object({ organizationId: OrganizationRef }),
  idempotent: true,
  response: PayoutOnboarding,
});

export const listOrganizationProperties = defineEndpoint({
  method: 'GET',
  path: '/internal/organizations/:organizationId/properties',
  mount: 'internal',
  summary: 'The apartments one client holds, with each code state and guest entry (OPS-003)',
  params: z.object({ organizationId: OrganizationRef }),
  query: PageRequest,
  response: InternalPropertyPage,
});

/**
 * The printed sheet for one apartment, as a page an operator can open.
 *
 * A POST because it MINTS a document: the sheet is rendered from the apartment's
 * active code and stored, exactly as the owner's own reprint does (WEB-023). It
 * generates no new code - reprinting an existing one is not generation, and the
 * WEB-014 cap counts generation - so an operator viewing a sheet cannot consume
 * a client's allowance.
 */
export const viewPropertyQrSheet = defineEndpoint({
  method: 'POST',
  path: '/internal/properties/:propertyId/qr-sheet',
  mount: 'internal',
  summary: 'Render the printed sheet for one apartment, without minting a code (OPS-003, WEB-023)',
  params: z.object({ propertyId: Uuid }),
  idempotent: true,
  response: z.object({ printUrl: z.url() }),
});

/**
 * Every code one client holds, in ONE PDF: the owner's own "Download all QR
 * codes" (`downloadQrSheet`), from the mount an operator reaches every client
 * on. One worker function answers both, so the two portals cannot disagree
 * about which apartments the file holds - every one still in the portfolio
 * that already holds its code. It mints no code, so the WEB-014 cap is
 * untouched.
 */
export const downloadOrganizationQrSheet = defineEndpoint({
  method: 'POST',
  path: '/internal/organizations/:organizationId/qr-sheet',
  mount: 'internal',
  summary: 'Every apartment code of one client in one PDF, minting none (OPS-003, WEB-019)',
  params: z.object({ organizationId: OrganizationRef }),
  idempotent: true,
  response: QrPortfolioSheet,
});

// ---------------------------------------------------------------------------
// Storefront mode assignment (PRD OPS-013)
// ---------------------------------------------------------------------------

export const assignPropertyMode = defineEndpoint({
  method: 'POST',
  path: '/internal/properties/storefront-mode',
  mount: 'internal',
  summary: 'Put one client and its whole portfolio on one menu type; effective next scan (OPS-013)',
  /*
   * One client at a time (founder, 14 October 2026): its sold set becomes this
   * one type and every apartment follows.
   */
  body: z.object({ mode: StorefrontMode, organizationId: Uuid }),
  idempotent: true,
  response: z.object({ updated: z.int().nonnegative() }),
});

// ---------------------------------------------------------------------------
// Bakery locations (PRD OPS-003, OPS-013, BAK-010, BAK-011, BAK-027)
//
// The internal Bakeries destination is three tabs, and two of them list
// LOCATIONS rather than organizations: mode A shops, mode B shops, mode B
// houses. That is a filter on `organizations.storefront_mode`, a column that
// already exists and is immutable from creation (OPS-013) - there is no
// endpoint here that changes a bakery's mode, and there must never be one.
//
// A location row is the unit an operator acts on because it is the unit that
// bakes: a shop is suspended, re-addressed or given a different cutoff, and the
// organization behind it is a commercial party rather than a place. The two
// location tabs are one endpoint (`listBakeryLocations`) under two filters, so
// the columns cannot drift apart between them.
// ---------------------------------------------------------------------------

/**
 * One bakery shop as operations lists it.
 *
 * Two independent states, and conflating them is the bug this shape exists to
 * prevent: `active` is the LOCATION's own standing, which
 * `suspendBakeryLocation` below toggles, and `suspended` is its
 * ORGANIZATION's, which `suspendOrganization` toggles. A suspended house darkens
 * every one of its shops however active each one says it is, so a row that
 * carried only `active` could show a green shop that takes no orders and give
 * the operator no way to see why. The row shows both and says which is which.
 */
export const InternalBakeryLocationSummary = z.object({
  id: Uuid,
  bakeryOrgId: Uuid,
  /** The house this shop belongs to. The mode A tab has it and never shows it. */
  orgName: z.string().min(1).max(160),
  name: z.string().min(1).max(120),
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).nullable(),
  postalCode: z.string().min(1).max(20),
  city: z.string().min(1).max(100),
  /**
   * Where the courier collects. Resolved server-side from the address, never
   * accepted from a client (the same rule the partner address flow states):
   * `scripts/check-bakery-coordinates.mjs` fails CI on a seeded location with
   * no, NaN or non-Paris coordinates.
   */
  latitude: Latitude,
  longitude: Longitude,
  /** The shop's own zone; every wall-clock fact below is stated in it. */
  timezone: TimeZone,
  /** The organization's immutable mode (OPS-013); the tab, restated per row. */
  storefrontMode: BakeryStorefrontMode,
  /** The shop's own standing. False = suspended by an operator. */
  active: z.boolean(),
  /** The ORGANIZATION's manual suspension, so a dark row can say why it is dark. */
  suspended: z.boolean(),
  /**
   * The HOUSE's payout verification, restated per shop, for the payout column.
   */
  payoutVerification: PayoutVerificationState,
  /**
   * Why the HOUSE is Suspended, restated per shop: a shop sells under its
   * house, so a shop whose house is suspended takes no order however active it
   * says it is (founder, 2026-09-18; docs/finance/onboarding.html#suspension).
   */
  houseSuspendedFor: z.array(SuspensionReason),
  /**
   * How many devices are paired to this counter right now (device-pairing plan
   * 2.3, BAK-037). Zero is an ONBOARDING fact, not an outage - the OPS-011
   * offline-device alert and the readiness check are suppressed for a shop with
   * none, which is exactly why the roster has to show the number.
   *
   * On the SUMMARY rather than the detail, unlike `contactPhone` below: it is
   * the column an operator scans a list of new shops for.
   */
  deviceCount: z.int().nonnegative(),
  /**
   * What a courier cannot be booked without and this shop lacks
   * (`pickupReadinessOf`, BAK-035), in `PICKUP_REQUIREMENTS` order; empty when
   * it lacks nothing. A shop with any is unavailable for service - allocation
   * offers it to no guest (`PICKUP_INCOMPLETE`) - so the row reads Incomplete
   * and names these beside the pill (founder, 2026-10-07; HZFAB2).
   */
  pickupMissing: z.array(z.enum(PICKUP_REQUIREMENTS)),
  createdAt: Instant,
});
export type InternalBakeryLocationSummary = z.infer<typeof InternalBakeryLocationSummary>;

/**
 * What the shops tabs' Device filter asks of a row: has this counter paired
 * anything. The roster carries a COUNT and nothing about reachability - that is
 * Active bakeries' question, off the readiness rows - so the filter is the two
 * halves of the column it sits over, "No device" and everything else.
 */
export const BakeryLocationDeviceFilter = z.enum(['PAIRED', 'UNPAIRED']);
export type BakeryLocationDeviceFilter = z.infer<typeof BakeryLocationDeviceFilter>;

/**
 * What the State filter asks: the OUTCOME the row's one badge prints. A shop
 * is Suspended when it is off or its house is suspended; which of the two
 * darkened it is not a thing the list distinguishes (founder, 2026-08-29).
 *
 * Otherwise it reads what its house reads, off the same projection
 * (`organizationStanding`), so the two tabs cannot disagree about one bakery.
 * `bakeryLocationStateOf` returns an `OrganizationStanding` INTO this type,
 * which is what fails the typecheck if the two lists ever drift.
 *
 * INCOMPLETE is the shop's own word and has no house counterpart: the shop is
 * on and its house trades, but it lacks what a courier is booked on - a
 * contact mobile, an address or coordinates (`pickupMissing`) - so allocation
 * offers it to nobody (BAK-035, founder 2026-10-07). Below Suspended, because
 * a suspension is the decision an operator has to lift first.
 */
export const BakeryLocationStateFilter = z.enum(['SUSPENDED', 'INCOMPLETE', 'ACTIVE']);
export type BakeryLocationStateFilter = z.infer<typeof BakeryLocationStateFilter>;

type BakeryLocationStanding = Pick<
  InternalBakeryLocationSummary,
  'active' | 'houseSuspendedFor' | 'deviceCount' | 'pickupMissing'
>;

/** The Device filter's value for a row. One derivation, for the worker and the screen. */
export const bakeryLocationDeviceOf = (row: BakeryLocationStanding): BakeryLocationDeviceFilter =>
  row.deviceCount === 0 ? 'UNPAIRED' : 'PAIRED';

/** The State filter's value for a row, and the word its badge prints. */
export const bakeryLocationStateOf = (
  row: Pick<BakeryLocationStanding, 'active' | 'houseSuspendedFor' | 'pickupMissing'>,
): BakeryLocationStateFilter => {
  if (!row.active) return 'SUSPENDED';
  const house: BakeryLocationStateFilter = organizationStanding({
    suspendedFor: row.houseSuspendedFor,
  });
  if (house === 'SUSPENDED') return house;
  return row.pickupMissing.length > 0 ? 'INCOMPLETE' : house;
};

export const listBakeryLocations = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-locations',
  mount: 'internal',
  summary: 'Bakery shops, filtered by storefront mode; backs both location tabs (OPS-003)',
  query: PageRequest.extend({
    /**
     * The tab. Omitted means every mode, which no tab asks for and a support
     * search might: the two tabs are exclusive (OPS-013) and each sends its own.
     */
    mode: StorefrontMode.optional(),
    /** Matches the shop name, its house's name or its address. */
    search: z.string().max(120).optional(),
    /** Shops of any of these houses; omitted or empty means every house. Repeated on the wire. */
    house: z.array(Uuid).max(200).optional(),
    /** Any of these device standings. */
    device: z
      .array(BakeryLocationDeviceFilter)
      .max(BakeryLocationDeviceFilter.options.length)
      .optional(),
    /** Any of these states. */
    state: z
      .array(BakeryLocationStateFilter)
      .max(BakeryLocationStateFilter.options.length)
      .optional(),
  }),
  response: pageOf(InternalBakeryLocationSummary).extend({
    /**
     * What the three filters can offer, and how many shops carry each (an option
     * no shop carries is not offered). Counted over every shop the mode and the
     * search match - never one page - each with the other two filters applied
     * and its own ignored, so ticking one house leaves the others on offer.
     * `house` carries the name beside the id, because a house on offer may have
     * no shop on the page the screen holds.
     */
    facets: z.object({
      house: z.array(FilterFacet.extend({ label: z.string().min(1).max(160) })),
      device: z.array(FilterFacet),
      state: z.array(FilterFacet),
    }),
  }),
});

/**
 * Everything settable about one shop, for the detail screen the row name opens
 * (plan 3.4). The service facts are the ones BAK-010, BAK-011 and BAK-027 give
 * a shop - the same ones the baker's own portal edits, and stated here so an
 * operator configures a shop from one screen rather than from whichever portal
 * happens to reach it. A mode A shop has no house that signs in anywhere, so
 * for those this screen is the ONLY place these are set at all.
 *
 * Closures are the one part that is not a field of this shape: they are a list
 * with an add and a remove rather than a box, so they keep their own two
 * endpoints below and travel on this shape read-only.
 */
export const InternalBakeryLocationInfo = InternalBakeryLocationSummary.extend({
  /**
   * The shop's ONE number, E.164: what the courier calls at pickup (RTE-013)
   * and where the platform writes to whoever runs this counter - the pairing
   * QR, the PIN and the "a new device was paired" notice (BAK-035).
   *
   * It was two fields until 2026-08-23, `contactPhone` for the courier and this
   * one for the manager, on the reasoning that the first legitimately holds a
   * shop landline nobody reads an SMS on. The data never bore that out: of the
   * thirty-two shops in the demo world, thirty-one had the courier line set and
   * the mobile empty, and the one that had both held the SAME number in each.
   * So the distinction cost two boxes on every shop screen, both labelled with
   * no hint of which was which, and bought nothing - and the open-shop reminder
   * was already texting the "courier line" anyway. One number, and it has to be
   * a mobile, because a mobile can do both jobs and a landline can only do one.
   *
   * Nullable, and cleared by an explicit null on the PATCH. A shop with none
   * books no courier at all - the delivery provider refuses to create a
   * delivery with no pickup phone number - so the screen shows the gap rather
   * than hiding it, and a code cannot be SENT until somebody types a
   * destination into the send dialog, which saves it here as it sends.
   */
  contactEmail: Email.nullable(),
  contactMobile: PhoneE164.nullable(),
  /**
   * The seven weekday pickup spans (BAK-027), exactly the shape the baker's
   * portal and the tablet submit. Always seven members, Monday first.
   */
  pickupWindows: z.array(PickupWindowRule).length(7),
  /**
   * The date ranges this shop is shut (BAK-010). Read-only on this shape - the
   * two endpoints below are what change it.
   */
  closures: z.array(Closure),
  /**
   * BAK-003 and BAK-004, the two clocks operations administers for every
   * counter rather than per shop. Shown read-only here for the same reason the
   * tablet shows them: an operator reading a shop's response windows should see
   * all of them, not the subset this screen happens to set.
   */
  immediateAcceptTimeoutMinutes: BakerySettings.shape.immediateAcceptTimeoutMinutes,
  /** Taken from the bakery contract's own shape, so the internal screen and the
   * tablet cannot disagree about the permitted values or the timeout ceiling. */
  acceptanceMode: BakerySettings.shape.acceptanceMode,
  acceptanceTimeoutMinutes: BakerySettings.shape.acceptanceTimeoutMinutes,
  /** The order camera: whether this counter photographs the bag at Ready. */
  bagPhotoPolicy: BakerySettings.shape.bagPhotoPolicy,
  /** The shop's service radius (OPS-004): drawn as a circle on the coverage map
   * for the operator. It decides nothing about allocation or whether an order
   * can be placed (founder, 2026-09-26). */
  serviceRadiusMeters: z.int().positive().max(50_000),
  /** The one capacity the platform models, per BAK-011. */
  maxPickupsPerHour: z.int().min(1).max(240),
  /**
   * What this counter has actually been taking to prepare a bag, in minutes,
   * over the last week of service days (BAK-014, `PREPARATION_WINDOW_DAYS`).
   *
   * It sits beside `maxPickupsPerHour` because that is the dial it is evidence
   * for: the hourly cap is an operator's judgement about how much a counter can
   * absorb, and until this field it was made with nothing measured in front of
   * it.
   *
   * THE FIGURE IN FORCE, not a report about it: the counter's own p75 once it
   * has bagged enough orders, and the platform seed until then - the same two
   * functions the courier booking calls. It showed the MEDIAN until 2026-08-25,
   * which meant the evidence on this screen and the promise made from it were
   * different statistics, a few minutes apart, with nothing saying so.
   */
  preparationMinutes: z.number().positive(),
  /** False while the figure above is the seed rather than this shop's own. */
  preparationMeasured: z.boolean(),
  /**
   * Whether `deleteBakeryLocation` would succeed, and the count that decides
   * it (OPS-003). The Delete permanently entry is present and DISABLED when
   * this is false, stating why - "still holding N orders" - rather than absent,
   * so the operator learns the rule instead of hunting for the control. The
   * server checks the same fact again; this is the affordance, not the gate.
   *
   * PENDING orders, not every order the shop ever took (founder decision
   * 2026-08-30). It was the second until that decision, which made a shop
   * undeletable for ever the day it sold its first croissant; a shop that has
   * finished everything it was holding goes, and its past goes nowhere.
   */
  deletable: z.boolean(),
  pendingOrderCount: z.int().nonnegative(),
});
export type InternalBakeryLocationInfo = z.infer<typeof InternalBakeryLocationInfo>;

export const getBakeryLocationInfo = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-locations/:locationId',
  mount: 'internal',
  summary: 'One shop with its address, service configuration and delete eligibility (OPS-003)',
  params: z.object({ locationId: Uuid }),
  response: InternalBakeryLocationInfo,
});

/** One measured order on a shop's preparation history: a dot on the chart. */
export const InternalPreparationHistoryOrder = z.object({
  orderId: Uuid,
  shortCode: z.string().max(10),
  /** When the bag was marked ready - where the dot sits along the time axis. */
  readyAt: Instant,
  /** Start of preparation to Ready, in fractional minutes (BAK-014). */
  minutes: z.number().nonnegative(),
});
export type InternalPreparationHistoryOrder = z.infer<typeof InternalPreparationHistoryOrder>;

/**
 * Six months of one shop's measured preparation times, with the mean and the
 * p75 over the same orders (BAK-014). Only orders measured end to end are
 * listed - an order with no Ready tap has no preparation time, not a zero one.
 * The two figures are over this window and are NOT the figure in force, which
 * is `InternalBakeryLocationInfo.preparationMinutes` over seven days.
 */
export const InternalPreparationHistory = z.object({
  from: ServiceDay,
  to: ServiceDay,
  /** Oldest first. */
  orders: z.array(InternalPreparationHistoryOrder),
  averageMinutes: z.number().nonnegative().nullable(),
  p75Minutes: z.number().nonnegative().nullable(),
});
export type InternalPreparationHistory = z.infer<typeof InternalPreparationHistory>;

export const getBakeryLocationPreparationHistory = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-locations/:locationId/preparation-history',
  mount: 'internal',
  summary: "One shop's measured preparation time per order over six months (BAK-014)",
  params: z.object({ locationId: Uuid }),
  response: InternalPreparationHistory,
});

/** The identity an operator types in on the create form. */
const BakeryLocationIdentity = z.object({
  name: z.string().min(1).max(120),
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).nullable(),
  postalCode: z.string().min(1).max(20),
  city: z.string().min(1).max(100),
  /**
   * The shop's address and mobile (BAK-035), OPTIONAL on the create form for
   * the reason the service facts are absent from it altogether: a new shop is
   * four lines of an address, not a configuration exercise, and losing a whole
   * address to a mistyped email would be the worst trade on this screen. The
   * send dialog fills them in later, as it sends.
   */
  contactEmail: Email.nullable().optional(),
  contactMobile: PhoneE164.nullable().optional(),
});

/**
 * OPS-003 / plan 2.2: creating a shop, and the two ways it happens.
 *
 * A discriminated union rather than an optional `bakeryOrgId`, for the reason
 * `createOrganization` refines rather than trusting a handler: the two cases
 * have OPPOSITE requirements, and an optional field can express neither.
 *
 *   mode B - the house exists first, on its own tab, and every shop is attached
 *            to one. `bakeryOrgId` is REQUIRED, and the server additionally
 *            refuses an organization that is absent, is not a BAKERY, or is not
 *            mode B (plan 3.5).
 *   mode A - the mode A tab shows one row per LOCATION and never names an
 *            organization, so the organization must not be a second thing an
 *            operator creates. The org and its single location are written in
 *            ONE transaction and the org takes the location's name; there is no
 *            org id to send, and sending one is not a shape this schema has.
 *
 * The service facts (cutoff, acceptance, radius, capacity) are not on this
 * body: a new shop is created carrying the platform defaults and an admin sets
 * its own on the detail screen, so the create form stays the four lines of an
 * address rather than a configuration exercise.
 */
export const createBakeryLocation = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations',
  mount: 'internal',
  summary: 'Create a shop; mode A creates its house with it, mode B attaches to one (OPS-003)',
  body: z.discriminatedUnion('storefrontMode', [
    BakeryLocationIdentity.extend({ storefrontMode: z.literal('A') }),
    BakeryLocationIdentity.extend({ storefrontMode: z.literal('B'), bakeryOrgId: Uuid }),
  ]),
  idempotent: true,
  response: InternalBakeryLocationInfo,
});

/**
 * Everything a PATCH may change on a shop. Deliberately NOT the whole info
 * shape: the coordinates are re-resolved from the address by the server, the
 * mode is immutable (OPS-013), and `active`, `deletable` and `pendingOrderCount` are
 * facts rather than settings - each has its own endpoint or none at all.
 */
export const BakeryLocationEditableFields = z.object({
  name: InternalBakeryLocationInfo.shape.name,
  addressLine1: InternalBakeryLocationInfo.shape.addressLine1,
  addressLine2: InternalBakeryLocationInfo.shape.addressLine2,
  contactEmail: InternalBakeryLocationInfo.shape.contactEmail,
  contactMobile: InternalBakeryLocationInfo.shape.contactMobile,
  postalCode: InternalBakeryLocationInfo.shape.postalCode,
  city: InternalBakeryLocationInfo.shape.city,
  /**
   * The seven weekday spans, submitted whole (BAK-027). A weekday left out of
   * the array keeps what it had; `closed` clears both of its times. Whole-map
   * rather than per-day because that is the shape the baker's portal, the
   * tablet and this screen all already speak, and a second shape for the same
   * fact is how the two ends come to disagree about what a closed day means.
   */
  pickupWindows: InternalBakeryLocationInfo.shape.pickupWindows,
  acceptanceMode: InternalBakeryLocationInfo.shape.acceptanceMode,
  acceptanceTimeoutMinutes: InternalBakeryLocationInfo.shape.acceptanceTimeoutMinutes,
  bagPhotoPolicy: InternalBakeryLocationInfo.shape.bagPhotoPolicy,
  serviceRadiusMeters: InternalBakeryLocationInfo.shape.serviceRadiusMeters,
  maxPickupsPerHour: InternalBakeryLocationInfo.shape.maxPickupsPerHour,
});
export type BakeryLocationEditableFields = z.infer<typeof BakeryLocationEditableFields>;

export const updateBakeryLocation = defineEndpoint({
  method: 'PATCH',
  path: '/internal/bakery-locations/:locationId',
  mount: 'internal',
  summary: 'Edit a shop address, pickup windows, acceptance or capacity (OPS-003, BAK-027)',
  params: z.object({ locationId: Uuid }),
  /*
   * PATCH semantics via `patchSchema` (../patch): omitted is untouched, a value
   * sets, and the three nullables - `addressLine2`, `contactEmail` and
   * `contactMobile` - clear on an explicit null. Never a bespoke
   * `!== undefined` check: an operator correcting a postcode must not silently
   * reset the acceptance timeout they never opened.
   */
  body: patchSchema(BakeryLocationEditableFields),
  idempotent: false,
  response: InternalBakeryLocationInfo,
});

/**
 * The shop's closures, from the operator's side (BAK-010).
 *
 * Two endpoints rather than a field on the PATCH above, and the same two the
 * baker's own portal has: a closure is added and removed, never edited, and
 * expressing an add-and-remove as a whole-array field would make "remove the
 * August shutdown" and "I loaded this screen before somebody else added one"
 * the same request. Same handlers, same audit rows, different mount.
 */
export const addBakeryLocationClosure = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/closures',
  mount: 'internal',
  summary: 'Record a closure date range for one shop (OPS-003, BAK-010)',
  params: z.object({ locationId: Uuid }),
  body: ClosureRangeBody,
  idempotent: true,
  response: ClosureRangeResult,
});

export const deleteBakeryLocationClosure = defineEndpoint({
  method: 'DELETE',
  path: '/internal/bakery-locations/:locationId/closures/:id',
  mount: 'internal',
  summary: 'Remove a recorded closure from one shop (OPS-003, BAK-010)',
  params: z.object({ locationId: Uuid, id: Uuid }),
  idempotent: false,
  response: Ok,
});

/**
 * The `/b` and `/c` twin (BAK-047): one day on the calendar, closed or
 * re-opened, ranges split and merged on the server. The reply is the shop's
 * whole closure list, so the screen repaints from it and asks for nothing.
 */
export const setBakeryLocationClosureDay = defineEndpoint({
  method: 'PUT',
  path: '/internal/bakery-locations/:locationId/closures/day',
  mount: 'internal',
  summary: 'Close or re-open one day for one shop (OPS-003, BAK-047)',
  params: z.object({ locationId: Uuid }),
  body: ClosureDayBody,
  idempotent: true,
  response: ClosureDayResult,
});

/**
 * The Activate / Suspend half of the row menu (plan 3.3), mirroring
 * `suspendOrganization`.
 *
 * `suspended` in this BODY is an instruction about the LOCATION - the shop's
 * own `active` becomes its opposite. It is not the `suspended` field on the
 * response, which is the organization's and which no location-level activation
 * can override: reinstating a shop under a suspended house leaves it dark, and
 * the row says so.
 */
export const suspendBakeryLocation = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/suspend',
  mount: 'internal',
  summary: 'Suspend or reinstate one shop; a suspended shop wins no allocation (OPS-003)',
  params: z.object({ locationId: Uuid }),
  body: z.object({ suspended: z.boolean() }),
  idempotent: true,
  response: InternalBakeryLocationSummary,
});

/**
 * Delete a shop that never traded (plan 2.1). Same rule and same reasoning as
 * `deleteOrganization` above: REFUSED when the location has ever carried an
 * order, because an order snapshots the shop it was baked by (CAT-006) and
 * `bakery_locations` is `onDelete: 'restrict'`. Suspension is what a traded
 * shop gets.
 */
export const deleteBakeryLocation = defineEndpoint({
  method: 'DELETE',
  path: '/internal/bakery-locations/:locationId',
  mount: 'internal',
  summary: 'Delete a shop that never carried an order; refused otherwise (OPS-003)',
  params: z.object({ locationId: Uuid }),
  idempotent: false,
  response: Ok,
});

// ---------------------------------------------------------------------------
// Pairing a counter device (docs/plans/device-pairing.html 2.3, OPS-025)
//
// Seven endpoints, all under one location, all reached from that location row's
// three-dot menu. Every shape they use is stated in `../device-pairing` and
// reused verbatim by the bakery house's own `/c/locations/:locationId/...`
// endpoints, because it is one dialog served to two audiences and a shape
// restated per mount is a shape that drifts.
//
// The three actions are kept APART on that menu on purpose (plan 2.5): Send
// re-sends the current code and touches nothing; Regenerate mints a new code
// and kills the old one, and still touches no paired device; Remove is per
// device and is the ONLY thing that invalidates a token.
// ---------------------------------------------------------------------------

export const getLocationPairing = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-locations/:locationId/pairing',
  mount: 'internal',
  summary: 'This shop pairing code, PIN state and paired devices (OPS-024, BAK-033)',
  params: z.object({ locationId: Uuid }),
  response: LocationPairing,
});

export const regeneratePairingCode = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pairing/regenerate',
  mount: 'internal',
  summary: 'Mint a new pairing code; the old one dies, no paired device is touched (BAK-033)',
  params: z.object({ locationId: Uuid }),
  idempotent: true,
  response: PairingCodeIssued,
});

export const sendPairingCode = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pairing/send',
  mount: 'internal',
  summary: 'Email or text this shop pairing code to its manager (BAK-036)',
  params: z.object({ locationId: Uuid }),
  body: SendPairingMessage,
  idempotent: false,
  response: PairingMessageSent,
});

export const sendDevicePin = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pin/send',
  mount: 'internal',
  summary: 'Email or text this shop PIN to its manager; 400 until one is chosen (BAK-036)',
  params: z.object({ locationId: Uuid }),
  body: SendPairingMessage,
  idempotent: false,
  response: PairingMessageSent,
});

/**
 * Mark a PIN reset pending. Deliberately does NOT clear the PIN: a shop with no
 * PIN pairs on the code alone, so clearing would put a working bakery back into
 * the first-install state and let whoever holds the QR enroll a phone in the
 * minutes before the counter chose a new one (plan 2.4).
 */
export const resetDevicePin = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pin/reset',
  mount: 'internal',
  summary: 'Ask this counter to choose a new PIN; the old one keeps working until it does',
  params: z.object({ locationId: Uuid }),
  idempotent: true,
  response: DevicePinReset,
});

/**
 * Forgive the lockout: the counter may type again, immediately (OPS-026).
 *
 * The 06:10 call is "it will not let us in", and until this existed the only
 * two answers were "wait an hour" and a reset that asks staff who KNOW the code
 * to invent a new one on a device none of them can operate. It changes no
 * credential: it deletes the throttle record and nothing else.
 */
export const clearDevicePinLockout = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pin/unlock',
  mount: 'internal',
  summary: 'Clear this counter PIN lockout so it may type again now; changes no credential',
  params: z.object({ locationId: Uuid }),
  idempotent: true,
  response: DevicePinLockoutCleared,
});

/**
 * Choose this shop's PIN on the counter's behalf, and optionally send it.
 *
 * The overwrite `resetDevicePin` deliberately is not: a house whose staff have
 * all forgotten the code needs a code they are TOLD, not a keypad prompt. It
 * clears any pending reset and forgives the throttle in the same write, because
 * a counter given a new code must not meet the old code's lockout.
 */
export const setDevicePinByAdmin = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/pin/set',
  mount: 'internal',
  summary: 'Set this shop PIN as an operator; overwrites, clears a pending reset (BAK-036)',
  params: z.object({ locationId: Uuid }),
  body: SetDevicePinByAdmin,
  idempotent: true,
  response: DevicePinSetByAdmin,
});

export const revokeDevice = defineEndpoint({
  method: 'POST',
  path: '/internal/bakery-locations/:locationId/devices/:deviceId/revoke',
  mount: 'internal',
  summary: 'Remove one paired device; its token is dead on the next call (BAK-037)',
  params: z.object({ locationId: Uuid, deviceId: Uuid }),
  idempotent: true,
  response: DeviceRevoked,
});
